// src/utils/saisieEquipement.ts — SCE Issues
// Reconnaître une machine à partir des seuls chiffres de son N° de série
// (26/09, avec le clavier numérique du formulaire). Pur, testable.
//
// Au pavé numérique, on ne tape pas « -SC » : « 4825 » doit suffire quand
// une seule machine porte ce numéro. S'il y en a plusieurs (4825-SC et
// 4825-H, ou 1245-H et 1245-H-06), on ne choisit pas à la place de
// l'opérateur : il touche la bonne suggestion. Quatre chiffres exactement :
// à trois, « 482 » choisirait une 482-… pendant qu'on tape « 4825 ».

import { cleRapprochement, type Equipement, type Issue } from '@/types/issue.types'
import { estDeclarePar } from '@/utils/identiteDeclarant'

/** La seule machine dont le N° de série est « <chiffres>-… », ou null. */
export function serieParChiffres(equipements: readonly Equipement[], saisie: string): Equipement | null {
  const chiffres = saisie.trim()
  if (!/^\d{4}$/.test(chiffres)) return null
  const trouves = equipements.filter((eq) => cleRapprochement(eq.numeroSerie).startsWith(`${chiffres}-`))
  return trouves.length === 1 ? (trouves[0] ?? null) : null
}

// ─── L'équipement de ma dernière déclaration (19/10) ───
//
// Souvent, on déclare plusieurs problèmes de suite sur le même équipement :
// le formulaire reprend celui de MA dernière déclaration, si elle a moins de
// 24 h. Lu dans les problèmes (pas dans le navigateur) : vrai d'un appareil à
// l'autre, et sur la tablette partagée (déclarant relu par SaisiPar).

export const DUREE_REPRISE_EQUIPEMENT_MS = 24 * 60 * 60 * 1000

export interface EquipementRepris {
  numeroSerie: string
  /** Date de la déclaration d'où il vient (ISO). */
  le: string
}

/**
 * Le N° de série de ma déclaration la plus récente, si elle date de moins de
 * 24 h ; null sinon. Un défaut déclaré sur plusieurs équipements (Lot 14) :
 * celui de la première fiche du groupe, pas de la dernière créée.
 */
export function equipementDeMaDerniereDeclaration(
  issues: readonly Issue[],
  identites: readonly string[],
  maintenant: number,
): EquipementRepris | null {
  let derniere: Issue | null = null
  for (const i of issues) {
    if (!estDeclarePar(i.declarant, identites) || !i.numeroSerie.trim()) continue
    if (!derniere || Date.parse(i.dateDeclaration) > Date.parse(derniere.dateDeclaration)) derniere = i
  }
  if (!derniere) return null
  const age = maintenant - Date.parse(derniere.dateDeclaration)
  if (Number.isNaN(age) || age < 0 || age > DUREE_REPRISE_EQUIPEMENT_MS) return null
  const groupe = cleRapprochement(derniere.groupe)
  const premiere = groupe ? issues.find((i) => cleRapprochement(i.id) === groupe && i.numeroSerie.trim()) : undefined
  return { numeroSerie: (premiere ?? derniere).numeroSerie.trim(), le: derniere.dateDeclaration }
}
